import { DekError, identifyError } from "./error.js";
import { _addDefaultHeaders, _createUrl } from "./helper.js";
import { ResponseHandler } from './responseHandler.js';
/**
 * Třída pro zpracování API požadavků
 * @class ApiHandler
 * @template P - Typ cesty (cesty API)
 * @template Data - Typ očekávaných dat z API
 *
 * @example
 * ```typescript
 * const api = new ApiHandler({
 *   path: '/api/users',
 *   parameters: { page: 1, limit: 10 }
 * });
 * const users = await api.send();
 * ```
 */
class ApiHandler {
    fetchParams;
    settings = {};
    responseHandler = new ResponseHandler();
    response;
    /**
     * Konstruktor pro inicializaci API handleru
     * @param settings - Nastavení pro API požadavek
     * @throws {DekError} Pokud chybí povinné parametry
     */
    constructor(settings) {
        this.checkForRequired(settings);
        const defaultContentType = settings.body instanceof FormData ? false : settings.defaultContentType ?? true;
        this.fetchParams = {
            apiEndpoint: settings.apiEndpoint,
            path: settings.path,
            body: settings.body,
            parameters: settings.parameters,
            pathParameters: settings.pathParameters,
            options: {
                defaultContentType,
                method: settings.method ?? 'GET',
                signal: settings.signal,
                ...settings.options,
            }
        };
        this.settings = {
            silent: settings.silent ?? false,
            responseType: settings.responseType ?? 'json'
        };
        this.applyPathParameters();
        this.applySearchParameters();
        this.applyBody();
    }
    // #########################
    // # Validace před voláním #
    // #########################
    checkForRequired(settings) {
        try {
            if (!settings.path)
                throw new DekError('Path je povinný');
        }
        catch (error) {
            console.error(error);
            identifyError(error, new URL(settings.path, settings.apiEndpoint), settings.options ?? {}, !settings.silent);
            throw error;
        }
    }
    // ##############
    // # Úprava URL #
    // ##############
    applyPathParameters() {
        // V url je /article/faq/list/{parentSlug}
        // Tak se nahradí {parentSlug} s hodnotou z settings.pathParameters.parentSlug
        // Nahrazení parametrů v URL cestě
        if ('pathParameters' in this.fetchParams && this.fetchParams.pathParameters) {
            Object.entries(this.fetchParams.pathParameters).forEach(([key, value]) => {
                this.fetchParams.path = this.fetchParams.path.replace(`{${key}}`, value);
            });
            // Validace, že všechny placeholdery byly nahrazeny
            const remainingPlaceholders = this.fetchParams.path.match(/\{([^}]+)\}/g);
            if (remainingPlaceholders) {
                throw new DekError(`Nepodařilo se nahradit všechny parametry v URL: ${remainingPlaceholders.join(', ')}`);
            }
        }
    }
    applySearchParameters() {
        if ('parameters' in this.fetchParams && this.fetchParams.parameters) {
            const checkValue = (value) => typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
            const search = new URLSearchParams();
            for (const [key, value] of Object.entries((this.fetchParams.parameters))) {
                if (checkValue(value)) {
                    search.set(key, value.toString());
                }
                else if (Array.isArray(value)) {
                    for (const item of value) {
                        if (checkValue(item)) {
                            search.append(key, item.toString());
                        }
                        else {
                            console.warn(`Neznámý typ parametru: ${key} => ${item}`);
                        }
                    }
                }
                else if (typeof value !== 'undefined') {
                    // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
                    console.warn(`Neznámý typ parametru: ${key} => ${value}`);
                }
            }
            this.fetchParams.options.search = search;
        }
    }
    applyBody() {
        if ('body' in this.fetchParams && this.fetchParams.body) {
            if (typeof this.fetchParams.body === 'string' || this.fetchParams.body instanceof FormData) {
                this.fetchParams.options.body = this.fetchParams.body;
            }
            else {
                this.fetchParams.options.body = JSON.stringify(this.fetchParams.body);
            }
        }
    }
    // #######################
    // # Handlování response #
    // #######################
    /**
     * Nastaví výchozí handler pro úspěšnou odpověď (status 200)
     */
    setDefaultResponseHandler() {
        this.responseHandler.setDefaultResponseHandler(async () => this.getData());
    }
    /**
    * Přidá handler pro zpracování odpovědi s daným status kódem
    * @param statusCode - HTTP status kód nebo kategorie ('2xx', '4xx', '5xx')
    * @param callback - Funkce pro zpracování odpovědi
    * @returns this pro method chaining
    *
    * @example
    * ```typescript
    * api.addResponseHandler(404, async (response) => {
    *   console.log('Resource not found');
    *   return null;
    * });
    * ```
    */
    addResponseHandler(statusCode, callback) {
        this.responseHandler.addResponseHandler(statusCode, callback);
        return this;
    }
    // #####################
    // # Hlavní funkce API #
    // #####################
    /**
     * Odešle požadavek na server a zpracuje response
     * @returns Promise s daty z API nebo undefined
     * @throws {DekError} Pokud dojde k chybě při zpracování požadavku
     *
     * @example
     * ```typescript
     * try {
     *   const data = await api.send();
     *   console.log(data);
     * } catch (error) {
     *   console.error('API error:', error);
     * }
     * ```
     */
    async send() {
        const options = _addDefaultHeaders(this.fetchParams.options);
        const url = _createUrl(this.fetchParams.apiEndpoint, this.fetchParams.path, options.search);
        this.setDefaultResponseHandler();
        try {
            this.response = await fetch(url.toString(), options);
            this.responseHandler.setResponse(this.response);
            this.responseHandler.setOptions(this.fetchParams.options);
            return await this.responseHandler.runResponseHandlers();
        }
        catch (error) {
            console.error(error);
            const saveError = this.settings.silent === true
                ? false
                : Array.isArray(this.settings.silent)
                    ? !this.settings.silent.includes(this.response?.status ?? 0)
                    : true;
            identifyError(error, url, options, saveError);
            throw error;
        }
    }
    /**
     * Zpracuje chybu z response a zobrazí uživateli příslušnou zprávu
     * @param setting - Nastavení pro zobrazení chyby
     * @throws {DekError} S detaily chyby
     */
    async handleError(setting) {
        await this.responseHandler.handleError({ ...setting, options: this.fetchParams });
    }
    /**
     * Získá data z response podle nastaveného typu
     * @param force - Pokud true, vrátí data i při chybovém statusu
     * @returns Promise s daty nebo undefined
     * @throws {DekError} Pokud response není nastavena nebo není v pořádku
     */
    async getData(force) {
        if (!this.response)
            throw new DekError('Response není nastavena, byl volán send()?');
        if (!this.response.ok && !force)
            throw new DekError(`Response není v pořádku: ${this.response.status}`);
        try {
            const text = await this.response.clone().text();
            // Pokud je response null, tak se vrátí undefined
            if (text === '')
                return undefined;
            switch (this.settings.responseType) {
                case 'text':
                    return text;
                case 'json':
                    return await this.response.clone().json();
                case 'blob':
                    return await this.response.clone().blob();
                case 'arrayBuffer':
                    return await this.response.clone().arrayBuffer();
                default: {
                    const errorType = this.settings.responseType;
                    throw new DekError(`Neznámý typ response: ${errorType}`);
                }
            }
        }
        catch (error) {
            throw new DekError('Chyba při ApiHandler.getData: ' + error.message);
        }
    }
}
export { ApiHandler };
